import { FAMILY_LABEL } from '../data/families.js';
import { ADVANCED_PLANT_BY_ID } from '../data/advancedCapacity.js';
import {
  DEFAULT_GAME_MODE,
  GAME_VERSION_BY_MODE
} from '../data/modes.js';
import {
  createSubmissionKey,
  formatPlayerCountry,
  formatPlayerRole,
  GAME_VERSION
} from './scores.js';

export const ANALYTICS_ENDPOINT = '/.netlify/functions/analytics';

function cleanText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normaliseNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function familyMix(portfolio = []) {
  // ADVANCED_PLANT_BY_ID is a superset of the Classic roster (same
  // ids and families, plus Grid Battery), so it is safe for both modes.
  return portfolio.reduce((mix, id) => {
    const family = ADVANCED_PLANT_BY_ID[id]?.family;
    if (!family) return mix;
    const label = FAMILY_LABEL[family] ?? family;
    mix[label] = (mix[label] ?? 0) + 1;
    return mix;
  }, {});
}

function portfolioByYear(buildPlanByYear = {}) {
  return [1, 2, 3].map((year) => ({
    year,
    plants: Array.isArray(buildPlanByYear[year]) ? buildPlanByYear[year] : []
  }));
}

// Recorded alongside portfolioByYear so an Advanced run's plan can be
// RE-RATED later. Plan Rating is a function of (build plan, retire
// plan), and without the retirements a stored Advanced entry can only
// be re-rated as a plan that never retires - which is a different plan
// and a wrong rating. Classic has no retirements and yields {}.
function retirePlanByYear(retirePlanByYearState = {}) {
  return [1, 2, 3].map((year) => ({
    year,
    plants: Array.isArray(retirePlanByYearState[year])
      ? retirePlanByYearState[year]
      : []
  }));
}

function disasterDetails(disasters = []) {
  return disasters.map((disaster) => ({
    id: disaster?.id ?? null,
    name: disaster?.name ?? 'Unknown disaster',
    severityRoll: disaster?.severityRoll ?? null
  }));
}

function yearDetails(yearResults = []) {
  return yearResults.map((result, index) => ({
    year: result?.year ?? index + 1,
    disasters: disasterDetails(result?.disasters),
    variableOutputRolls: result?.rolledOutputs ?? {},
    demand: normaliseNumber(result?.demand),
    totalOutput: normaliseNumber(result?.totalOutput),
    shortfall: normaliseNumber(result?.shortfall),
    shortfallCost: normaliseNumber(result?.shortfallCost),
    capitalCost: normaliseNumber(result?.capital),
    resourceDevelopmentPremium: normaliseNumber(
      result?.resourceDevelopmentPremium
    ),
    operatingCost: normaliseNumber(result?.opex),
    emissions: normaliseNumber(result?.emissions),
    carbonEmissions: normaliseNumber(
      result?.carbonEmissions ?? result?.emissions
    ),
    usefulCleanFraction: normaliseNumber(result?.usefulCleanFraction),
    subsidyActive: Boolean(result?.subsidyActive),
    subsidyReformed: Boolean(result?.subsidyReformed),
    priceShock: Boolean(result?.priceShock)
  }));
}

export function createAnalyticsPayload({
  state,
  playerName,
  playerRole,
  playerCountry,
  leaderboardSubmissionStatus = 'unknown'
}) {
  const finalResult = state?.finalResult ?? {};
  const portfolio = state?.portfolio ?? [];
  const mode = state?.mode ?? DEFAULT_GAME_MODE;

  return {
    mode,
    gameVersion: GAME_VERSION_BY_MODE[mode] ?? GAME_VERSION,
    playerName: cleanText(playerName),
    playerRole: formatPlayerRole(playerRole),
    playerCountry: formatPlayerCountry(playerCountry),
    playerCountryCode: cleanText(playerCountry).toUpperCase(),
    submissionKey: createSubmissionKey({
      state,
      playerName,
      playerRole,
      playerCountry
    }),
    totalCost: finalResult.totalCost,
    finalPortfolio: portfolio,
    portfolioByYear: portfolioByYear(state?.buildPlanByYear),
    retirePlanByYear: retirePlanByYear(state?.retirePlanByYear),
    plantFamilyMix: familyMix(portfolio),
    years: yearDetails(state?.yearResults),
    cop31Outcome: {
      id: state?.cop30?.id ?? null,
      headline: state?.cop30?.headline ?? '',
      bonusMultiplier: state?.cop30?.bonusMultiplier ?? null,
      penaltyMultiplier: state?.cop30?.penaltyMultiplier ?? null
    },
    emissions: finalResult.emissions,
    shortfall: finalResult.shortfall,
    shortfallCost: finalResult.shortfallCost,
    // Advanced Capacity Mode only; undefined (dropped by JSON) in Classic.
    capacityShortfall: finalResult.capacityShortfall,
    capacityShortfallCost: finalResult.capacityShortfallCost,
    capitalCost: finalResult.capital,
    resourceDevelopmentPremium:
      finalResult.resourceDevelopmentPremium ?? 0,
    operatingCost: finalResult.opex,
    carbonAmount: finalResult.carbonAmount,
    cleanEnergyTransitionAward: finalResult.cleanEnergyAward,
    systemResilienceAward: finalResult.resilienceAward,
    affordabilityAward: finalResult.affordabilityAward ?? 0,
    leaderboardSubmissionStatus
  };
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function submitAnalytics(
  payload,
  { fetcher = globalThis.fetch } = {}
) {
  if (typeof fetcher !== 'function') {
    throw new Error('Analytics are unavailable.');
  }

  const response = await fetcher(ANALYTICS_ENDPOINT, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new Error(
      body?.error ?? `Analytics request failed with status ${response.status}.`
    );
  }
  return body;
}
